<template>
  <div class="page-card">
    <h2 class="page-title">审核中心</h2>

    <div class="toolbar">
      <div class="toolbar-left">
        <el-select v-model="type" style="width: 180px" @change="handleTypeChange">
          <el-option label="商家认证审核" value="merchant-auth" />
          <el-option label="艺人实名审核" value="artist-realname" />
          <el-option label="艺人资料审核" value="artist-profile" />
          <el-option label="星途档案审核" value="artist-star-profile" />
          <el-option label="通告审核" value="casting-notice" />
        </el-select>
        <el-select
          v-model="query.status"
          clearable
          placeholder="请选择状态"
          style="width: 160px"
          @change="handleFilterChange"
        >
          <el-option
            v-for="item in statusOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
        <el-input
          v-model="query.keyword"
          placeholder="请输入关键字"
          clearable
          style="width: 220px"
          @keyup.enter.native="handleFilterChange"
          @clear="handleFilterChange"
        />
        <el-button type="primary" @click="handleFilterChange">查询</el-button>
        <el-button @click="resetQuery">重置</el-button>
      </div>
    </div>

    <el-table :data="pagedList" border v-loading="loading">
      <el-table-column prop="type" label="审核类型" width="160">
        <template slot-scope="{ row }">
          {{ typeText(row.type) }}
        </template>
      </el-table-column>
      <el-table-column prop="submitterName" label="提交主体" />
      <el-table-column prop="submitTime" label="提交时间" width="180" />
      <el-table-column prop="status" label="状态" width="140">
        <template slot-scope="{ row }">
          <el-tag :type="statusTagType(row.status)">
            {{ statusText(row.status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="updatedAt" label="更新时间" width="180" />
      <el-table-column label="操作" width="140">
        <template slot-scope="{ row }">
          <el-button type="text" @click="goDetail(row)">查看详情</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination-wrap">
      <el-pagination
        background
        layout="total, prev, pager, next"
        :total="list.length"
        :page-size="pageSize"
        :current-page="query.page"
        @current-change="handlePageChange"
      />
    </div>
  </div>
</template>

<script>
import {
  getArtistProfileAudits,
  getArtistStarProfileAudits,
  getArtistRealnameAudits,
  getCastingNoticeAudits,
  getMerchantAuthAudits
} from '../../api/audit'
import { navigateWithFallback } from '../../utils/admin-navigation'

const DEFAULT_TYPE = 'merchant-auth'
const DEFAULT_STATUS = 1
const DEFAULT_PAGE = 1
const PAGE_SIZE = 10

export default {
  name: 'AuditPage',
  data() {
    return {
      type: DEFAULT_TYPE,
      list: [],
      loading: false,
      pageSize: PAGE_SIZE,
      query: {
        status: DEFAULT_STATUS,
        keyword: '',
        page: DEFAULT_PAGE
      }
    }
  },
  computed: {
    pagedList() {
      const start = (this.query.page - 1) * this.pageSize
      return this.list.slice(start, start + this.pageSize)
    },
    statusOptions() {
      if (this.type === 'casting-notice') {
        return [
          { label: '待审核', value: 1 },
          { label: '已驳回', value: 3 },
          { label: '招募中', value: 5 },
          { label: '已下架', value: 6 },
          { label: '已封禁', value: 9 }
        ]
      }
      return [
        { label: '待审核', value: 1 },
        { label: '已通过', value: 2 },
        { label: '已驳回', value: 3 },
        { label: '待补充材料', value: 4 }
      ]
    }
  },
  created() {
    this.restoreRouteState()
    this.fetchData()
  },
  watch: {
    '$route.query': {
      deep: true,
      handler(newQuery, oldQuery) {
        const previousType = this.type
        const previousStatus = this.query.status
        const previousKeyword = this.query.keyword
        this.restoreRouteState()
        if (
          this.type !== previousType ||
          this.query.status !== previousStatus ||
          this.query.keyword !== previousKeyword
        ) {
          this.fetchData()
        } else if ((newQuery && newQuery.type) !== (oldQuery && oldQuery.type)) {
          this.fetchData()
        }
      }
    }
  },
  methods: {
    restoreRouteState() {
      const routeQuery = this.$route.query || {}
      this.type = routeQuery.type || DEFAULT_TYPE
      this.query.status = routeQuery.status ? Number(routeQuery.status) : DEFAULT_STATUS
      this.query.keyword = routeQuery.keyword || ''
      this.query.page = routeQuery.page ? Number(routeQuery.page) : DEFAULT_PAGE
    },
    buildListQuery() {
      return {
        type: this.type,
        status: String(this.query.status || DEFAULT_STATUS),
        page: String(this.query.page || DEFAULT_PAGE),
        keyword: this.query.keyword || undefined
      }
    },
    syncRouteQuery() {
      const nextQuery = this.buildListQuery()
      const current = this.$route.query || {}
      if (
        current.type === nextQuery.type &&
        current.status === nextQuery.status &&
        current.page === nextQuery.page &&
        (current.keyword || undefined) === nextQuery.keyword
      ) {
        return false
      }
      this.$router.replace({ path: this.$route.path, query: nextQuery })
      return true
    },
    getAuditFetcher() {
      if (this.type === 'merchant-auth') return getMerchantAuthAudits
      if (this.type === 'artist-realname') return getArtistRealnameAudits
      if (this.type === 'artist-profile') return getArtistProfileAudits
      if (this.type === 'artist-star-profile') return getArtistStarProfileAudits
      return getCastingNoticeAudits
    },
    typeText(type) {
      if (type === 'merchant-auth') return '商家认证'
      if (type === 'artist-realname') return '艺人实名'
      if (type === 'artist-profile') return '艺人资料'
      if (type === 'artist-star-profile') return '星途档案'
      if (type === 'casting-notice') return '通告审核'
      return type || '-'
    },
    statusText(status) {
      if (this.type === 'casting-notice') {
        if (status === 2) return '待审核'
        if (status === 3) return '招募中'
        if (status === 4) return '已驳回'
        if (status === 5) return '招募中'
        if (status === 6) return '已下架'
        if (status === 9) return '已封禁'
      } else {
        if (status === 1) return '待审核'
        if (status === 2) return '已通过'
        if (status === 3) return '已驳回'
        if (status === 4) return '待补充材料'
      }
      return status == null ? '-' : String(status)
    },
    statusTagType(status) {
      if (this.type === 'casting-notice') {
        if (status === 2) return 'warning'
        if (status === 3 || status === 5) return 'success'
        if (status === 4 || status === 9) return 'danger'
        if (status === 6) return 'info'
      } else {
        if (status === 1) return 'warning'
        if (status === 2) return 'success'
        if (status === 3) return 'danger'
        if (status === 4) return 'info'
      }
      return 'info'
    },
    handleTypeChange() {
      this.query.status = DEFAULT_STATUS
      this.query.page = DEFAULT_PAGE
      const changed = this.syncRouteQuery()
      if (!changed) {
        this.fetchData()
      }
    },
    handleFilterChange() {
      this.query.page = DEFAULT_PAGE
      const changed = this.syncRouteQuery()
      if (!changed) {
        this.fetchData()
      }
    },
    handlePageChange(page) {
      this.query.page = page
      this.syncRouteQuery()
    },
    resetQuery() {
      this.query = {
        status: DEFAULT_STATUS,
        keyword: '',
        page: DEFAULT_PAGE
      }
      this.type = DEFAULT_TYPE
      const changed = this.syncRouteQuery()
      if (!changed) {
        this.fetchData()
      }
    },
    async fetchData() {
      this.loading = true
      try {
        const params = {
          status: this.query.status,
          keyword: this.query.keyword || undefined
        }
        this.list = await this.getAuditFetcher()(params)
        const maxPage = Math.max(1, Math.ceil(this.list.length / this.pageSize))
        if (this.query.page > maxPage) {
          this.query.page = maxPage
          this.syncRouteQuery()
        }
      } catch (error) {
        this.$message.error(error.message || '加载审核列表失败')
      } finally {
        this.loading = false
      }
    },
    goDetail(row) {
      const query = {
        fromType: this.type,
        fromStatus: String(this.query.status || DEFAULT_STATUS),
        fromPage: String(this.query.page || DEFAULT_PAGE)
      }
      if (this.type === 'merchant-auth') {
        navigateWithFallback(this, { path: `/audits/merchant-auth/${row.id}`, query })
      } else if (this.type === 'artist-realname') {
        navigateWithFallback(this, { path: `/audits/artist-realname/${row.id}`, query })
      } else if (this.type === 'artist-profile') {
        navigateWithFallback(this, { path: `/audits/artist-profile/${row.id}`, query })
      } else if (this.type === 'artist-star-profile') {
        navigateWithFallback(this, { path: `/audits/artist-star-profile/${row.id}`, query })
      } else {
        navigateWithFallback(this, { path: `/audits/casting-notice/${row.id}`, query })
      }
    }
  }
}
</script>

<style scoped>
.pagination-wrap {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}
</style>
